import { get } from "aws-amplify/api";
import Moment from "moment";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button, Col, Form, Modal, Row } from "react-bootstrap";
import DatePicker from "react-datepicker";
import Select from "react-select";
import { PacmanLoader } from "react-spinners";
import { createColumnHelper } from "@tanstack/react-table";
import { GenericTable } from "components/GenericTable";
import { PAGE_SIZES } from "utils/pageSizeConfig";
import "react-datepicker/dist/react-datepicker.css";
import "containers/common/List.css";
import "containers/common/Table.css";

const columnHelper = createColumnHelper();

function MergerSubsListExport({ isAuthenticated }) {
  const [isLoading, setIsLoading] = useState(true);
  const [mergerSubs, setMergerSubs] = useState([]);
  const [ui, setUi] = useState({ showAlert: false, errorMessage: "" });
  const [selectedCityscope, setSelectedCityscope] = useState(null);
  const [cityscopeOptions, setCityscopeOptions] = useState([]);
  const [createdDateFrom, setCreatedDateFrom] = useState(null);
  const [createdDateTo, setCreatedDateTo] = useState(null);
  const [cityscope, setCityscope] = useState(null);

  const extractBody = async (result) => {
    try {
      if (result?.body) {
        return await result.body.json();
      }

      if (result?.response?.body) {
        return await result.response.body.json();
      }

      return result;
    } catch (error) {
      console.error("Failed to parse API response", error);
      return [];
    }
  };

  const getCityscopes = useCallback(async () => {
    const response = await get({
      apiName: "colin",
      path: "/cityscopes/dropdown",
    }).response;
    return extractBody(response);
  }, []);

  const getMergerSubs = useCallback(async (filter = {}) => {
    const filteredParams = Object.fromEntries(
      Object.entries(filter).filter(([_, value]) => value !== null && value !== "")
    );

    const response = await get({
      apiName: "colin",
      path: "/mergersubs",
      options: {
        queryParams: filteredParams,
      },
    }).response;
    return extractBody(response);
  }, []);

  const sortMergerSubs = useCallback((rows = []) => {
    return [...rows]
      .sort((a, b) => {
        if (a.created_date === b.created_date) {
          return 0;
        }
        return a.created_date > b.created_date ? -1 : 1;
      })
      .sort((a, b) => {
        if (a.cityscope === b.cityscope) {
          return 0;
        }
        return a.cityscope < b.cityscope ? -1 : 1;
      });
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      setIsLoading(false);
      return;
    }

    let mounted = true;

    const initialize = async () => {
      setIsLoading(true);
      try {
        const localCityscopes = await getCityscopes();
        if (mounted) {
          setCityscopeOptions(localCityscopes || []);
        }
      } catch (error) {
        console.error(error);
        if (mounted) {
          setUi({ showAlert: true, errorMessage: "Unable to load cityscope options." });
        }
      } finally {
        if (mounted) {
          document.title = "Mergers & Sub Divisions";
          setIsLoading(false);
        }
      }
    };

    initialize();

    return () => {
      mounted = false;
    };
  }, [getCityscopes, isAuthenticated]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsLoading(true);

    try {
      const localRows = await getMergerSubs({
        cityscope,
        createdDateFrom: createdDateFrom ? Moment(createdDateFrom).local().format("YYYY-MM-DD") : "",
        createdDateTo: createdDateTo ? Moment(createdDateTo).local().format("YYYY-MM-DD") : "",
      });

      setMergerSubs(sortMergerSubs(localRows));
    } catch (error) {
      console.error(error);
      setUi({ showAlert: true, errorMessage: "Unable to load merger/sub export data." });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCityscopeChange = (selected) => {
    setSelectedCityscope(selected);
    setCityscope(selected ? selected.map((item) => item.value).join(",") : null);
  };

  const getPropertyKeysByDirection = (details = [], direction) => {
    return details
      .reduce((filtered, detail) => {
        const sourceOrDest = detail.source_or_dest || detail.source_destination;
        if (sourceOrDest === direction && detail.property_key) {
          filtered.push(detail.property_key);
        }
        return filtered;
      }, [])
      .sort((a, b) => {
        const cityscopeA = String(a || "").toUpperCase().substring(0, 1);
        const cityscopeB = String(b || "").toUpperCase().substring(0, 1);
        if (cityscopeA < cityscopeB) {
          return -1;
        }
        if (cityscopeA > cityscopeB) {
          return 1;
        }

        const mapNoA = String(a || "").toUpperCase().substring(2, 3);
        const mapNoB = String(b || "").toUpperCase().substring(2, 3);
        if (mapNoA < mapNoB) {
          return -1;
        }
        if (mapNoA > mapNoB) {
          return 1;
        }

        const propertyNoA = String(a || "").toUpperCase().substring(6, 7);
        const propertyNoB = String(b || "").toUpperCase().substring(6, 7);
        if (propertyNoA < propertyNoB) {
          return -1;
        }
        if (propertyNoA > propertyNoB) {
          return 1;
        }

        return 0;
      })
      .join("; ");
  };

  const exportRecords = () => {
    if (!createdDateFrom || !createdDateTo) {
      setUi({
        showAlert: true,
        errorMessage: "You must enter created date from and to before running an export.",
      });
      return;
    }

    const typeMap = {
      M: "Merger",
      S: "Subdivision",
      N: "New Property",
      R: "Removed Property",
    };

    const exportRows = (mergerSubs || []).map((row) => ({
      ID: row.merger_sub_id,
      Cityscope: row.cityscope,
      Appendix: row.appendix,
      Type: typeMap[row.type],
      "Created Date": row.created_date ? Moment(row.created_date).local().format("DD-MM-YYYY") : "",
      "As they were": getPropertyKeysByDirection(row.mergersubs_list || [], "S"),
      "What they became": getPropertyKeysByDirection(row.mergersubs_list || [], "D"),
      Description: row.description,
    }));

    const fromDt = new Date(createdDateFrom);
    const toDt = new Date(createdDateTo);
    const fileName = `CS_MS_${fromDt.getFullYear()}${String(fromDt.getMonth() + 1).padStart(2, "0")}${String(fromDt.getDate()).padStart(2, "0")}_to_${toDt.getFullYear()}${String(toDt.getMonth() + 1).padStart(2, "0")}${String(toDt.getDate()).padStart(2, "0")}.csv`;

    const headers = [
      "ID",
      "Cityscope",
      "Appendix",
      "Type",
      "Created Date",
      "As they were",
      "What they became",
      "Description",
    ];

    const csvBody = [
      headers.join(","),
      ...exportRows.map((record) =>
        headers
          .map((field) => {
            const value = record[field] ?? "";
            if (typeof value === "string") {
              return `"${value.replace(/"/g, '""')}"`;
            }
            return value;
          })
          .join(",")
      ),
    ].join("\r\n");

    const blob = new Blob([csvBody], { type: "text/csv;charset=utf-8;" });

    if (navigator.msSaveBlob) {
      navigator.msSaveBlob(blob, fileName);
      return;
    }

    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", fileName);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const columns = useMemo(
    () => [
      columnHelper.accessor("merger_sub_id", {
        id: "merger_sub_id",
        header: "ID",
        size: 80,
      }),
      columnHelper.accessor("cityscope", {
        id: "cityscope",
        header: "Cityscope",
        size: 100,
      }),
      columnHelper.accessor("appendix", {
        id: "appendix",
        header: "Appendix",
        size: 120,
      }),
      columnHelper.accessor("type", {
        id: "type",
        header: "Type",
        size: 110,
      }),
      columnHelper.accessor("description", {
        id: "description",
        header: "Description",
        size: 260,
      }),
      columnHelper.accessor("created_date", {
        id: "created_date",
        header: "Created Date",
        cell: (info) => {
          const createdDate = info.getValue();
          return createdDate ? Moment(createdDate).local().format("DD-MM-YYYY") : "";
        },
        size: 110,
      }),
    ],
    []
  );

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="mergersubs">
      <Modal show={ui.showAlert} onHide={() => setUi((prev) => ({ ...prev, showAlert: false }))} centered>
        <Modal.Header closeButton>
          <Modal.Title>Uh Oh!</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="danger" className="mb-0">
            {ui.errorMessage}
          </Alert>
        </Modal.Body>
      </Modal>

      <Form onSubmit={handleSubmit}>
        <Row className="mergersubs-filter-row">
          <Col sm={4} md={4} lg={2}>
            <Form.Group as={Row} controlId="cityscope">
              <Col>
                <Select
                  value={selectedCityscope}
                  onChange={handleCityscopeChange}
                  options={cityscopeOptions}
                  placeholder="Cityscope"
                  isMulti
                  className="mergersubs-cityscope-select"
                  classNamePrefix="mergersubs-cityscope-select"
                />
              </Col>
            </Form.Group>
          </Col>

          <Col sm={4} md={4} lg={3}>
            <Form.Group as={Row} controlId="createdDateFrom">
              <Form.Label column className="text-right">
                Created Date From
              </Form.Label>
              <Col>
                <DatePicker
                  selected={createdDateFrom}
                  onChange={setCreatedDateFrom}
                  name="createdDateFrom"
                  dateFormat="dd/MM/yyyy"
                  className="form-control"
                  isClearable
                />
              </Col>
            </Form.Group>
          </Col>

          <Col sm={4} md={4} lg={3}>
            <Form.Group as={Row} controlId="createdDateTo">
              <Form.Label column className="text-right">
                Created Date To
              </Form.Label>
              <Col>
                <DatePicker
                  selected={createdDateTo}
                  onChange={setCreatedDateTo}
                  name="createdDateTo"
                  dateFormat="dd/MM/yyyy"
                  className="form-control"
                  isClearable
                />
              </Col>
            </Form.Group>
          </Col>

          <Col xs="auto">
            <Button type="submit" variant="primary" className="mergersubs-search-button">
              Search
            </Button>
          </Col>

          <Col xs="auto" className="text-right ml-auto">
            <Button
              type="button"
              variant="primary"
              className="mergersubs-search-button"
              onClick={exportRecords}
            >
              Export File
            </Button>
          </Col>
        </Row>
      </Form>

      <Row className="show-Container mt-3">
        <Col sm={12} md={12} lg={12}>
          {isLoading ? (
            <div className="outer-div">
              <div className="sweet-loading inner-div">
                <PacmanLoader
                  cssOverride={{ display: "block", margin: "0 auto" }}
                  size={20}
                  color="#e03c31"
                  loading={isLoading}
                />
              </div>
            </div>
          ) : (
            <GenericTable
              columns={columns}
              data={mergerSubs}
              pageSize={PAGE_SIZES.mergerSubsExport}
            />
          )}
        </Col>
      </Row>
    </div>
  );
}

export default MergerSubsListExport;